import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { ExcelReviewModalComponent } from '../../../../../_shared/components/modals/excel-review-modal/excel-review-modal.component';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { LookupMake, LookupModel, LookupPartCategory, LookupPartType, LookupSupplier, LookupWarehouse } from '../../../../../_shared/models/lookup.model';
import { toWholeNumber } from '../../../../../_shared/helpers/excel-upload.helper';
import { PartsAndInventoryService } from '../../../services/parts-and-inventory.service';
import { UpsertSupplierModalComponent } from '../../../../suppliers/components/modals/upsert-supplier-modal/upsert-supplier-modal.component';
import { AddWarehouseModalComponent } from '../add-warehouse-modal/add-warehouse-modal.component';
import { ExistingPartNumber, PartExcelRow, PartImport, ReviewPartField, ReviewPartItem } from '../../../models/part-import.model';
import {
  findByName,
  parseDateInput,
  toDecimal,
  toIsoDateString,
  toReviewPartItems,
  validateReviewPartItem
} from '../../../helpers/parts-upload.helper';

@Component({
  selector: 'app-parts-review-excel-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbDropdownModule,
    FlatpickrDatePickerComponent,
    ExcelReviewModalComponent,
    TranslocoPipe
  ],
  templateUrl: './parts-review-excel-modal.component.html',
  styleUrl: './parts-review-excel-modal.component.scss'
})
export class PartsReviewExcelModalComponent implements OnInit, OnDestroy {
  @Input() rows: PartExcelRow[] = [];
  @Input() createdBy = '';

  private activeModal = inject(NgbActiveModal);
  private modalService = inject(NgbModal);
  private lookupService = inject(LookupService);
  private partsService = inject(PartsAndInventoryService);
  private errorService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private cancellation = new Subject<void>();
  private transloco = inject(TranslocoService);

  items: ReviewPartItem[] = [];
  selected: ReviewPartItem | null = null;

  makes: LookupMake[] = [];
  partTypes: LookupPartType[] = [];
  categories: LookupPartCategory[] = [];
  storageLocations: LookupWarehouse[] = [];
  suppliers: LookupSupplier[] = [];
  modelsByMakeId = new Map<number, LookupModel[]>();
  private loadingModelMakeIds = new Set<number>();
  /** "warehouseId|oem" keys already in use — parts are unique per OEM number within a storage location. */
  private existingPartKeys = new Set<string>();
  private partNumberCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private partNumberCheckVersion = 0;

  isLoading = true;
  isImporting = false;
  isRefreshingSuppliers = false;
  isRefreshingStorageLocations = false;
  serverErrors: string[] = [];

  makeSearchTerm = '';
  modelSearchTerm = '';
  partTypeSearchTerm = '';
  storageLocationSearchTerm = '';
  supplierSearchTerm = '';
  readonly today = toIsoDateString(new Date());

  matchesSearch = (item: ReviewPartItem, term: string): boolean =>
    [item.partName, item.partNumber, item.make, item.model, item.partType, item.category, item.storageLocation]
      .some(value => value.toLowerCase().includes(term));

  itemName = (item: ReviewPartItem): string => item.partName.trim() || item.partNumber.trim();

  ngOnInit(): void {
    this.items = toReviewPartItems(this.rows);
    this.loadLookupsAsync();
  }

  ngOnDestroy(): void {
    if (this.partNumberCheckTimer) {
      clearTimeout(this.partNumberCheckTimer);
    }
    this.cancellation.next();
    this.cancellation.complete();
  }

  private async loadLookupsAsync(): Promise<void> {
    try {
      const partNumbers = [...new Set(this.items.map(item => item.partNumber.trim()).filter(Boolean))];
      const [makesResponse, partTypesResponse, categoriesResponse, warehousesResponse, suppliersResponse, existingResponse] = await Promise.all([
        this.lookupService.getMakesAsync(),
        this.lookupService.getPartTypesAsync(),
        this.lookupService.getPartCategoriesAsync(),
        this.lookupService.getWarehousesAsync([]),
        this.lookupService.getSuppliersAsync([]),
        partNumbers.length > 0
          ? this.partsService.getExistingPartNumbersAsync(partNumbers, this.cancellation)
          : Promise.resolve({ data: [] as ExistingPartNumber[] })
      ]);
      this.makes = makesResponse.data ?? [];
      this.partTypes = partTypesResponse.data ?? [];
      this.categories = categoriesResponse.data ?? [];
      this.storageLocations = warehousesResponse.data ?? [];
      this.suppliers = suppliersResponse.data ?? [];
      (existingResponse.data ?? []).forEach(existing => this.existingPartKeys.add(this.partKey(existing.partNumber, existing.warehouseId)));

      await this.loadModelsForItemsAsync();

      this.validateAll();
      this.selected = this.items.find(item => item.errors.length > 0) ?? this.items[0] ?? null;
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private async loadModelsForItemsAsync(): Promise<void> {
    const makeIds = new Set<number>();
    for (const item of this.items) {
      const make = findByName(this.makes, item.make);
      if (make) makeIds.add(make.id);
    }
    await Promise.all([...makeIds].map(makeId => this.ensureModelsLoadedAsync(makeId)));
  }

  private async ensureModelsLoadedAsync(makeId: number): Promise<void> {
    if (this.modelsByMakeId.has(makeId) || this.loadingModelMakeIds.has(makeId)) return;
    this.loadingModelMakeIds.add(makeId);
    try {
      const response = await this.lookupService.getModelsByMakeIdAsync(makeId);
      this.modelsByMakeId.set(makeId, response.data ?? []);
    } finally {
      this.loadingModelMakeIds.delete(makeId);
    }
  }

  get errorCount(): number {
    return this.items.filter(item => item.errors.length > 0).length;
  }

  get canImport(): boolean {
    return !this.isImporting
      && this.items.length > 0
      && this.errorCount === 0;
  }

  get filteredMakes(): LookupMake[] {
    return this.filterByName(this.makes, this.makeSearchTerm);
  }

  get filteredSuppliers(): LookupSupplier[] {
    return this.filterByName(this.suppliers, this.supplierSearchTerm);
  }

  /** Stock details only apply when the selected row has opening stock. */
  get selectedHasOpeningStock(): boolean {
    return !!this.selected && (toWholeNumber(this.selected.onHand) ?? 0) > 0;
  }

  get filteredPartTypes(): LookupPartType[] {
    return this.filterByName(this.partTypes, this.partTypeSearchTerm);
  }

  get filteredStorageLocations(): LookupWarehouse[] {
    return this.filterByName(this.storageLocations, this.storageLocationSearchTerm);
  }

  filteredModels(item: ReviewPartItem): LookupModel[] {
    return this.filterByName(this.modelsForItem(item), this.modelSearchTerm);
  }

  private filterByName<T extends { name: string }>(items: T[], term: string): T[] {
    const query = term.trim().toLowerCase();
    return query ? items.filter(item => item.name.toLowerCase().includes(query)) : items;
  }

  hasFieldError(item: ReviewPartItem, field: ReviewPartField): boolean {
    return item.errors.some(error => error.field === field);
  }

  knownMake(item: ReviewPartItem): LookupMake | undefined {
    return findByName(this.makes, item.make);
  }

  modelsForItem(item: ReviewPartItem): LookupModel[] {
    const make = this.knownMake(item);
    return make ? this.modelsByMakeId.get(make.id) ?? [] : [];
  }

  isLoadingModels(item: ReviewPartItem): boolean {
    const make = this.knownMake(item);
    return !!make && this.loadingModelMakeIds.has(make.id);
  }

  onItemRemoved(): void {
    this.validateAll();
  }

  onFieldChanged(field: ReviewPartField, value: string | number | null): void {
    if (!this.selected) return;
    this.selected[field] = value === null || value === undefined ? '' : `${value}`;
    this.validateAll();
    if (field === 'partNumber') {
      this.schedulePartNumberCheck(this.selected.partNumber);
    }
  }

  private schedulePartNumberCheck(partNumber: string): void {
    if (this.partNumberCheckTimer) {
      clearTimeout(this.partNumberCheckTimer);
    }
    const trimmed = partNumber.trim();
    if (!trimmed) return;

    const version = ++this.partNumberCheckVersion;
    this.partNumberCheckTimer = setTimeout(async () => {
      try {
        const response = await this.partsService.getExistingPartNumbersAsync([trimmed], this.cancellation);
        if (version !== this.partNumberCheckVersion || (response.data?.length ?? 0) === 0) return;
        // Remember every warehouse this OEM number is used in, so changing the storage location re-validates without another call.
        (response.data ?? []).forEach(existing => this.existingPartKeys.add(this.partKey(existing.partNumber, existing.warehouseId)));
        this.validateAll();
      } catch {
        // The upload itself re-validates OEM numbers, so a failed live check is not fatal here.
      }
    }, 400);
  }

  private partKey(partNumber: string, warehouseId: number): string {
    return `${warehouseId}|${partNumber.trim().toLowerCase()}`;
  }

  async selectMakeAsync(make: LookupMake): Promise<void> {
    if (!this.selected) return;
    this.selected.make = make.name;
    this.makeSearchTerm = '';
    await this.loadModelsAndValidateAsync(make.id);
  }

  selectModel(model: LookupModel): void {
    if (!this.selected) return;
    this.selected.model = model.name;
    this.modelSearchTerm = '';
    this.validateAll();
  }

  selectPartType(partType: LookupPartType): void {
    if (!this.selected) return;
    this.selected.partType = partType.name;
    this.partTypeSearchTerm = '';
    const category = this.categories.find(c => c.id === partType.categoryId);
    if (category) {
      this.selected.category = category.name;
    }
    this.validateAll();
  }

  selectSupplier(supplier: LookupSupplier | null): void {
    if (!this.selected) return;
    this.selected.supplier = supplier?.name ?? '';
    this.supplierSearchTerm = '';
    this.validateAll();
  }

  /** Opens the compact Add Warehouse modal; on save the storage-location list is reloaded and the new one selected. */
  openAddWarehouseModal(): void {
    const modalRef = this.modalService.open(AddWarehouseModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'add-warehouse-modal-window'
    });
    modalRef.closed.subscribe((result: number | boolean) => {
      if (result) {
        void this.refreshStorageLocationsAsync(typeof result === 'number' ? result : null);
      }
    });
  }

  private async refreshStorageLocationsAsync(newWarehouseId: number | null): Promise<void> {
    this.isRefreshingStorageLocations = true;
    const knownIds = new Set(this.storageLocations.map(warehouse => warehouse.id));
    try {
      const response = await this.lookupService.getWarehousesAsync([]);
      this.storageLocations = response.data ?? [];

      // Select the warehouse that was just added for the row being edited.
      const added = newWarehouseId !== null
        ? this.storageLocations.find(warehouse => warehouse.id === newWarehouseId)
        : this.storageLocations.filter(warehouse => !knownIds.has(warehouse.id)).at(0);
      if (added && this.selected) {
        this.selected.storageLocation = added.name;
      }

      this.validateAll();
      this.isRefreshingStorageLocations = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isRefreshingStorageLocations = false;
        }
      });
    }
  }

  /** Opens the same Add Supplier modal as the Suppliers page; on save the supplier list is reloaded. */
  openAddSupplierModal(): void {
    const modalRef = this.modalService.open(UpsertSupplierModalComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'supplier-modal-window'
    });
    modalRef.closed.subscribe((saved: boolean) => {
      if (saved) {
        void this.refreshSuppliersAsync();
      }
    });
  }

  private async refreshSuppliersAsync(): Promise<void> {
    this.isRefreshingSuppliers = true;
    const knownIds = new Set(this.suppliers.map(supplier => supplier.id));
    try {
      const response = await this.lookupService.getSuppliersAsync([]);
      this.suppliers = response.data ?? [];

      // When exactly one supplier was added, pick it for the row being edited.
      const added = this.suppliers.filter(supplier => !knownIds.has(supplier.id));
      if (added.length === 1 && this.selected && this.selectedHasOpeningStock) {
        this.selected.supplier = added[0].name;
      }

      this.validateAll();
      this.isRefreshingSuppliers = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isRefreshingSuppliers = false;
        }
      });
    }
  }

  selectCategory(category: LookupPartCategory): void {
    if (!this.selected) return;
    this.selected.category = category.name;
    this.validateAll();
  }

  selectStorageLocation(warehouse: LookupWarehouse): void {
    if (!this.selected) return;
    this.selected.storageLocation = warehouse.name;
    this.storageLocationSearchTerm = '';
    this.validateAll();
  }

  async onApplySuggestionsAsync(item: ReviewPartItem): Promise<void> {
    for (const error of item.errors) {
      if (error.suggestion) {
        item[error.suggestion.field as ReviewPartField] = error.suggestion.value;
      }
    }
    const make = this.knownMake(item);
    await this.loadModelsAndValidateAsync(make?.id);
  }

  private async loadModelsAndValidateAsync(makeId: number | undefined): Promise<void> {
    this.validateAll();
    if (makeId === undefined || this.modelsByMakeId.has(makeId)) return;
    try {
      await this.ensureModelsLoadedAsync(makeId);
    } catch (error: any) {
      this.errorService.handleError(error);
      return;
    }
    this.validateAll();
  }

  private validateAll(): void {
    this.items.forEach(item => this.validateItem(item));
  }

  private validateItem(item: ReviewPartItem): void {
    item.errors = validateReviewPartItem(item, {
      makes: this.makes,
      modelsByMakeId: this.modelsByMakeId,
      partTypes: this.partTypes,
      categories: this.categories,
      storageLocations: this.storageLocations,
      suppliers: this.suppliers
    }, (key, params) => this.transloco.translate(key, params));

    // Parts are unique per OEM number within a storage location; the same OEM number may live in another warehouse.
    const partNumber = item.partNumber.trim().toLowerCase();
    const storageLocation = item.storageLocation.trim().toLowerCase();
    const warehouse = findByName(this.storageLocations, item.storageLocation);
    if (partNumber && warehouse && this.existingPartKeys.has(this.partKey(partNumber, warehouse.id))) {
      item.errors = [...item.errors, {
        message: this.transloco.translate('parts.modals.reviewExcel.duplicateOemInWarehouse', {
          partNumber: item.partNumber.trim(),
          warehouse: warehouse.name
        }),
        kind: 'incorrect',
        field: 'partNumber'
      }];
    }
    if (partNumber && this.items.some(other => other !== item
      && other.partNumber.trim().toLowerCase() === partNumber
      && other.storageLocation.trim().toLowerCase() === storageLocation)) {
      item.errors = [...item.errors, {
        message: this.transloco.translate('parts.modals.reviewExcel.duplicateOemInUpload'),
        kind: 'incorrect',
        field: 'partNumber'
      }];
    }
  }

  async importAsync(): Promise<void> {
    if (!this.canImport) return;
    this.isImporting = true;
    this.serverErrors = [];
    try {
      await this.partsService.bulkUploadPartsAsync({ parts: this.toPartImports() }, this.cancellation);
      this.toastr.success(this.transloco.translate('parts.modals.reviewExcel.toastImported'));
      this.activeModal.close('imported');
    } catch (error: any) {
      if (error?.error?.errorCode === 'parts-inventory/invalid-parts-upload') {
        this.serverErrors = error.error.data?.length > 0
          ? error.error.data
          : [error.error.message || this.transloco.translate('parts.modals.reviewExcel.importRejected')];
        this.isImporting = false;
        return;
      }

      this.errorService.handleError(error, {
        callback: () => {
          this.isImporting = false;
        }
      });
    }
  }

  private toPartImports(): PartImport[] {
    const createdAt = new Date();
    return this.items.map(item => {
      const make = findByName(this.makes, item.make);
      const model = make ? findByName(this.modelsByMakeId.get(make.id) ?? [], item.model) : undefined;
      const partType = findByName(this.partTypes, item.partType);
      const category = findByName(this.categories, item.category);
      const warehouse = findByName(this.storageLocations, item.storageLocation);
      // Opening-stock details travel only with opening stock; the backend ignores them otherwise anyway.
      const onHand = toWholeNumber(item.onHand) ?? 0;
      const supplier = onHand > 0 ? findByName(this.suppliers, item.supplier) : undefined;
      const dateReceived = onHand > 0 ? parseDateInput(item.dateReceived) : null;
      return {
        partName: item.partName.trim(),
        partNumber: item.partNumber.trim(),
        makeId: make?.id ?? 0,
        make: make?.name ?? item.make.trim(),
        modelId: model?.id ?? 0,
        model: model?.name ?? item.model.trim(),
        modelYear: toWholeNumber(item.modelYear) ?? 0,
        description: item.description.trim(),
        partTypeId: partType?.id ?? 0,
        partType: partType?.name ?? item.partType.trim(),
        categoryId: category?.id ?? 0,
        category: category?.name ?? item.category.trim(),
        warehouseId: warehouse?.id ?? 0,
        storageLocation: warehouse?.name ?? item.storageLocation.trim(),
        reorderPoint: toWholeNumber(item.reorderPoint) ?? 0,
        reorderQuantity: toWholeNumber(item.reorderQuantity) ?? 0,
        maximumQuantity: toWholeNumber(item.maximumQuantity) ?? 0,
        onHand,
        unitCost: onHand > 0 ? toDecimal(item.unitCost) : null,
        supplierId: supplier?.id ?? null,
        supplier: onHand > 0 ? (supplier?.name ?? item.supplier.trim()) : '',
        dateReceived: dateReceived ? toIsoDateString(dateReceived) : null,
        createdBy: this.createdBy,
        createdAt
      };
    });
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
